// The three writes behind 不要這款 / 今天不做 / 加回 and the real 復原.
//
// Magnus 2026-09-15: 「when i press 帶回了 does it remove the stock?」 — it does (bring_back runs
// return_to_office and clears on_display), so 復原 must move the stock back instead of only
// unticking the card. It does that through POST /restock-v2/undo, which writes its own action row
// with `kind = 'undo'` and `undo_of`, inside a 12-hour window.
//
// Shape of every call here: ask, hide the row at once, then let the refetch decide. On failure the
// row comes back exactly as it was and the snackbar carries the API's own zh-TW sentence — a part-
// timer taps these dozens of times a day, so a row must never be left claiming something the stock
// does not agree with.
import { useCallback, useState } from 'react';
import { useSnackbar } from 'notistack';
import api from '../../../services/api';
import { newReference, readNokeError } from './format';
import {
  EXPIRED_UNDO, SKIP_KIND, VetoState, VetoVariant, EMPTY_VETO, dropVetoState, excludeStyleBody,
  excludedMoveKey, moveSkipKey, planVetoKey, skipTodayBody, tileSkipKey, unexcludeStyleBody,
  unskipTodayBody,
} from './exclusions';
import {
  VARIANT_KEEP_URL, VARIANT_MAIN_URL, VARIANT_READD_URL, VARIANT_REJECT_URL, VARIANT_UNKEEP_URL,
  VARIANT_UNMAIN_URL, variantKeepBody, variantKeepPendingKey, variantMainBody, variantMainPendingKey,
  variantPendingKey, variantReaddBody, variantUnkeepBody, variantUnmainBody, variantVetoBody,
  variantVetoKey,
} from './counterList';
import {
  RestockV2DoneRecord, RestockV2ExcludedMove, RestockV2Move, RestockV2VetoResponse,
} from './types';

const EXCLUDE_URL = '/api/noke/restock-v2/exclude';
const UNEXCLUDE_URL = '/api/noke/restock-v2/unexclude';
const UNDO_URL = '/api/noke/restock-v2/undo';

export interface Veto {
  state: VetoState;
  // Keyed exactly like VetoState, so a tile / card / line can disable its own control only.
  pending: Record<string, boolean>;
  excludeStyle: (sku: string) => Promise<void>;
  // 不要這個顏色／尺寸 from a rack tile or a 今天 card. Same endpoint the 選品 chips use, but routed
  // through here so it gets the optimistic window the other veto verbs have.
  excludeVariant: (sku: string, variant: VetoVariant) => Promise<void>;
  addBackVariant: (sku: string, variantId: number) => Promise<void>;
  // The two verbs a 帶回 TILE offers, plus the way back from each. 今天不做 reuses the existing
  // scope='today' row (no new endpoint); 留著 is the new permanent one.
  skipBringBack: (sku: string, variant: VetoVariant) => Promise<void>;
  unskipBringBack: (sku: string, variant: VetoVariant) => Promise<void>;
  keepVariant: (sku: string, variant: VetoVariant) => Promise<void>;
  unkeepVariant: (sku: string, variantId: number) => Promise<void>;
  // 設為主打 / 取消主打 — his standing colour preference for one style (Magnus 2026-09-19).
  // Routed through here, not useCounterList, so it gets the same optimistic window the other
  // per-variant verbs have: the plan takes ~6s to recompute and a badge that waits for it reads
  // as 「the tap did nothing」.
  setMainVariant: (sku: string, variant: VetoVariant) => Promise<void>;
  clearMainVariant: (sku: string, variant?: VetoVariant | null) => Promise<void>;
  // Called by the tab when a recompute LANDS, with the state as it was when that recompute was
  // REQUESTED. Only those decisions are retired — anything answered while it was in flight is
  // older than the plan that just arrived, so the plan cannot speak for it (see dropVetoState).
  retire: (landed: VetoState) => void;
  skipToday: (move: RestockV2Move) => Promise<void>;
  addBackStyle: (sku: string) => Promise<void>;
  addBackMove: (row: RestockV2ExcludedMove) => Promise<void>;
  undo: (move: RestockV2Move, record?: RestockV2DoneRecord) => Promise<boolean>;
}

const drop = (map: Record<string, true>, key: string): Record<string, true> => {
  const next = { ...map };
  delete next[key];
  return next;
};

/** The window from the /done response. No record (or no timestamp) = nothing to reverse. */
function undoExpired(record?: RestockV2DoneRecord): boolean {
  const until = record?.undoable_until ? new Date(record.undoable_until) : null;
  return !!until && Number.isFinite(until.getTime()) && until.getTime() <= Date.now();
}

export function useVeto(refetch: (immediate?: boolean) => void): Veto {
  const { enqueueSnackbar } = useSnackbar();
  const [state, setState] = useState<VetoState>(EMPTY_VETO);
  const [pending, setPending] = useState<Record<string, boolean>>({});

  const mark = useCallback((key: string, on: boolean) => {
    setPending((old) => { const next = { ...old }; if (on) next[key] = true; else delete next[key]; return next; });
  }, []);

  /**
   * One shape for all four veto verbs: hide → POST → refetch → forget the local hide (the plan that
   * came back owns it now). A failure un-hides and says why.
   * @param field which half of VetoState the optimistic key belongs to
   */
  const run = useCallback(async (
    field: 'plan' | 'today' | 'variant' | 'kept' | 'unkept' | 'restored' | 'main' | 'unmain',
    key: string, url: string, body: object,
    fallback: string, pendingKey?: string,
    opposite?: { field: 'kept' | 'unkept' | 'restored' | 'main' | 'unmain'; key: string },
  ) => {
    // The optimistic key and the busy key are the same for the three original verbs. A variant veto
    // splits them: the plan is keyed on the (sku, variant_id) PAIR, while the busy flag stays in the
    // `variant|…` namespace 選品's chips already use, so one decision greys one control everywhere.
    const busyKey = pendingKey || key;
    // 留著 → 取消留著 → 留著 within one optimistic window: the two maps are opposites, so the
    // reverse decision has to DROP the forward one, not sit on top of it. Without this, 取消留著
    // renders as nothing at all because the pin is still asserted.
    setState((old) => ({
      ...old,
      ...(opposite ? { [opposite.field]: drop(old[opposite.field], opposite.key) } : {}),
      [field]: { ...old[field], [key]: true },
    }));
    mark(busyKey, true);
    try {
      const response = await api.post<RestockV2VetoResponse>(url, body);
      const message = response.data?.message;
      enqueueSnackbar(message || fallback, { variant: 'success' });
      // The optimistic key is NOT dropped here any more. The refresh is coalesced (a burst of
      // decisions costs one recompute), so it may be seconds away; dropping the key now would make
      // the row he just removed flash back onto the screen until it lands. `clear` drops them all
      // the moment a plan that already knows about them arrives.
      refetch();
    } catch (error) {
      setState((old) => ({ ...old, [field]: drop(old[field], key) }));
      enqueueSnackbar(readNokeError(error, '沒有存到，請重試。'), { variant: 'error' });
      // A 404 means this row is not what the page thinks it is — the page is stale. Ask for a
      // recompute even though the write failed, or he keeps tapping a control that cannot work.
      if ((error as { response?: { status?: number } })?.response?.status === 404) refetch();
    } finally {
      mark(busyKey, false);
    }
  }, [enqueueSnackbar, mark, refetch]);

  const excludeStyle = useCallback(async (sku: string) => {
    await run('plan', sku, EXCLUDE_URL, excludeStyleBody(newReference('v2-exclude'), sku),
      '已排除這款，三個畫面都不會再出現');
  }, [run]);

  /**
   * 不要這個顏色／尺寸 — the narrow verb, raised from the tile or card that names that one colour.
   * It writes the SAME row 選品's chip writes (POST /list/variant/reject), so a veto set at the rail
   * is the same decision as one set at the desk; only the optimistic window is added here.
   */
  const excludeVariant = useCallback(async (sku: string, variant: VetoVariant) => {
    await run(
      'variant', variantVetoKey(sku, variant.variant_id), VARIANT_REJECT_URL,
      variantVetoBody(newReference('v2-list-variant-reject'), sku, variant),
      '已排除這個顏色／尺寸，其他顏色照常', variantPendingKey(sku, variant.variant_id),
    );
  }, [run]);

  const addBackVariant = useCallback(async (sku: string, variantId: number) => {
    await run(
      'restored', variantVetoKey(sku, variantId), VARIANT_READD_URL,
      variantReaddBody(newReference('v2-list-variant-readd'), sku, variantId),
      '已加回這個顏色／尺寸', variantPendingKey(sku, variantId),
    );
  }, [run]);

  /**
   * 今天不做 raised from a 帶回 TILE rather than from a 今天 card.
   *
   * Deliberately the SAME row the card writes (scope='today', move_kind='bring_back', the bare
   * sku+variant pair): a piece skipped at the rail must read as skipped on the walk screen too, and
   * a second 「rack-scoped skip」 concept would be two truths about one shelf.
   */
  const skipBringBack = useCallback(async (sku: string, variant: VetoVariant) => {
    await run(
      'today', tileSkipKey(sku, variant.variant_id), EXCLUDE_URL,
      skipTodayBody(newReference('v2-tile-skip'), sku, variant.variant_id, 'bring_back'),
      '今天不帶回這一件，明天會再出現', variantPendingKey(sku, variant.variant_id),
    );
  }, [run]);

  const unskipBringBack = useCallback(async (sku: string, variant: VetoVariant) => {
    await run(
      'restored', tileSkipKey(sku, variant.variant_id), UNEXCLUDE_URL,
      unskipTodayBody(newReference('v2-tile-unskip'), sku, variant.variant_id, 'bring_back'),
      '已恢復，今天還是要帶回', variantPendingKey(sku, variant.variant_id),
    );
  }, [run]);

  /**
   * 留著 — the permanent half. 「for the ones showing 帶回 allow me to remove so it doesn't show or
   * refute making it stay」 (Magnus 2026-09-17): this is the refute. The variant stays on the rail
   * and the engine stops proposing 帶回 for it until 取消留著.
   *
   * Its busy flag lives in its own `keep|…` namespace, NOT the shared `variant|…` one: 留著 and
   * 不要這個顏色／尺寸 are opposite decisions about the same pair, and one in flight must not grey
   * out the other's control in 選品.
   */
  const keepVariant = useCallback(async (sku: string, variant: VetoVariant) => {
    await run(
      'kept', variantVetoKey(sku, variant.variant_id), VARIANT_KEEP_URL,
      variantKeepBody(newReference('v2-variant-keep'), sku, variant),
      '已留著，不會再排帶回', variantKeepPendingKey(sku, variant.variant_id),
      { field: 'unkept', key: variantVetoKey(sku, variant.variant_id) },
    );
  }, [run]);

  const unkeepVariant = useCallback(async (sku: string, variantId: number) => {
    await run(
      'unkept', variantVetoKey(sku, variantId), VARIANT_UNKEEP_URL,
      variantUnkeepBody(newReference('v2-variant-unkeep'), sku, variantId),
      '已取消留著，會重新排進帶回', variantKeepPendingKey(sku, variantId),
      { field: 'kept', key: variantVetoKey(sku, variantId) },
    );
  }, [run]);

  /**
   * 設為主打. SEVERAL per style since 2026-09-20, so this ADDS a colour rather than replacing
   * one. The pending 取消主打 it must drop is now the one on THIS colour (plus the legacy
   * whole-style clear), or 主打 → 取消 → 主打 on one chip inside a single window would render
   * as no pin. Busy is per chip, so pinning 黑色 no longer greys out 海軍藍's button.
   */
  const setMainVariant = useCallback(async (sku: string, variant: VetoVariant) => {
    setState((old) => ({
      ...old,
      unmain: Object.keys(old.unmain || {}).reduce((out: Record<string, true>, key) => {
        if (key !== variantVetoKey(sku, null)) out[key] = true;
        return out;
      }, {}),
    }));
    await run(
      'main', variantVetoKey(sku, variant.variant_id), VARIANT_MAIN_URL,
      variantMainBody(newReference('v2-variant-main'), sku, variant),
      '已加進主打；架上已有或不可訂的時候還是會換色',
      variantMainPendingKey(sku, variant.variant_id),
      { field: 'unmain', key: variantVetoKey(sku, variant.variant_id) },
    );
  }, [run]);

  /**
   * 取消主打 on ONE colour, or on the whole style when no variant is given (which is what the
   * verb meant while a style could hold only one). The optimistic half drops exactly the same
   * scope, so the chip stops showing 主打 on the tap rather than ~6s later.
   */
  const clearMainVariant = useCallback(async (sku: string, variant?: VetoVariant | null) => {
    const id = variant && variant.variant_id !== null && variant.variant_id !== undefined
      ? String(variant.variant_id) : null;
    setState((old) => ({
      ...old,
      main: Object.keys(old.main || {}).reduce((out: Record<string, true>, key) => {
        const mine = key.startsWith(`${sku}|`) && (id === null || key.slice(sku.length + 1) === id);
        if (!mine) out[key] = true;
        return out;
      }, {}),
    }));
    await run(
      'unmain', variantVetoKey(sku, id), VARIANT_UNMAIN_URL,
      variantUnmainBody(newReference('v2-variant-unmain'), sku, id),
      id ? '已取消這個主打' : '已取消主打，這款又回到依銷量排色',
      variantMainPendingKey(sku, id),
    );
  }, [run]);

  const retire = useCallback((landed: VetoState) => {
    setState((old) => dropVetoState(old, landed));
  }, []);

  const skipToday = useCallback(async (move: RestockV2Move) => {
    await run('today', moveSkipKey(move), EXCLUDE_URL,
      skipTodayBody(newReference('v2-skip'), move.line.sku, move.line.variant_id, SKIP_KIND[move.kind]),
      '今天不做這一件，明天會再出現');
  }, [run]);

  const addBackStyle = useCallback(async (sku: string) => {
    await run('restored', planVetoKey(sku), UNEXCLUDE_URL,
      unexcludeStyleBody(newReference('v2-unexclude'), sku), '已加回清單');
  }, [run]);

  const addBackMove = useCallback(async (row: RestockV2ExcludedMove) => {
    await run('restored', excludedMoveKey(row), UNEXCLUDE_URL,
      unskipTodayBody(newReference('v2-unskip'), row.sku, row.variant_id, row.move_kind), '已加回清單');
  }, [run]);

  /**
   * 復原 for a completed card. Returns true when the card should stay unticked — false puts it back,
   * because the stock write is still standing.
   */
  const undo = useCallback(async (move: RestockV2Move, record?: RestockV2DoneRecord): Promise<boolean> => {
    if (!record || !record.client_ref) {
      enqueueSnackbar(EXPIRED_UNDO, { variant: 'warning' });
      return false;
    }
    if (undoExpired(record)) {
      enqueueSnackbar(EXPIRED_UNDO, { variant: 'warning' });
      return false;
    }
    mark(`undo|${move.id}`, true);
    try {
      const response = await api.post<RestockV2VetoResponse>(UNDO_URL,
        { client_ref: newReference('v2-undo'), undo_client_ref: record.client_ref });
      enqueueSnackbar(response.data?.message || '已復原，庫存已改回', { variant: 'success' });
      return true;
    } catch (error) {
      // A 409/404 carries the server's own sentence (已經復原過了 / 超過 12 小時 / 庫存已變動) and the
      // forward move still stands, so the card goes back to 已完成 rather than lying.
      enqueueSnackbar(readNokeError(error, '沒辦法復原，請到庫存頁調整。'), { variant: 'error' });
      return false;
    } finally {
      mark(`undo|${move.id}`, false);
    }
  }, [enqueueSnackbar, mark]);

  return {
    state, pending, excludeStyle, excludeVariant, addBackVariant, skipBringBack, unskipBringBack,
    keepVariant, unkeepVariant, setMainVariant, clearMainVariant, skipToday, addBackStyle,
    addBackMove, undo, retire,
  };
}

export default useVeto;
